import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '~/providers/AppProvider';
import BoatItem from '~/components/boats/boatItem';
import styled from 'styled-components';
import type { BoatThumbnailDto } from '~/api/client/api';
import ReactPaginate from 'react-paginate';
import { useRouter } from 'next/router';
import { defaultBoatSearchOptions } from '~/api/config';
import BoatsSort from '~/components/filter/boatsSort';
import { Trans } from '@lingui/react';
import getConfig from 'next/config';
const { publicRuntimeConfig } = getConfig();
interface Props {
  boats: BoatThumbnailDto[];
  boatsTotalNumber: number;
  showPerPage?: number;
  isMapShownFullScreen: boolean;
  isMapShown: boolean;
  mapFullScreen?: boolean;
  hoveredResultId: string;
  setHoveredResultId: any;
  placeName: string;
  showTopWrapper?: boolean;
  showPagination?: boolean;
}

export default function BoatsList({
  boats,
  boatsTotalNumber,
  isMapShownFullScreen,
  isMapShown,
  hoveredResultId,
  setHoveredResultId,
  placeName,
  showTopWrapper = true,
  showPagination = true,
}: Props) {
  const router = useRouter();
  const appCtx = useContext(AppContext);

  const [boatsToShow, setBoatsToShow] = useState(boats);
  const [pageCount, setPageCount] = useState(Math.ceil(boatsTotalNumber / defaultBoatSearchOptions.pageSize));
  const [selectedPage, setSelectedPage] = useState(
    router.query.pageNumber === undefined ? 0 : parseInt(String(router.query.pageNumber)) - 1
  );

  useEffect(() => {
    setSelectedPage(router.query.pageNumber === undefined ? 0 : parseInt(String(router.query.pageNumber)) - 1);
  }, [router.query.pageNumber]);

  useEffect(() => {
    setPageCount(Math.ceil(boatsTotalNumber / defaultBoatSearchOptions.pageSize));
    setBoatsToShow(boats);
  }, [JSON.stringify(boats), boatsTotalNumber]);

  const handlePageClick = ({ selected }) => {
    appCtx.actions.setLoading(true);

    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        pageNumber: selected + 1,
        action: undefined,
      },
    });
  };

  return (
    <BoatsListWrapper isMapShownFullScreen={isMapShownFullScreen} isMapShown={isMapShown}>
      {showTopWrapper ? (
        <TopWrapper>
          {appCtx.state.loading ? (
            <BoatsNumber>
              <Trans id="boatsList.searchingBoatsIn" /> {placeName}...
            </BoatsNumber>
          ) : (
            <BoatsNumber>
              {boatsTotalNumber > 0 ? (
                <span>
                  {boatsTotalNumber}+ <Trans id="search.boatsFound" /> <Trans id="global.in" /> {placeName}
                </span>
              ) : (
                <span>
                  <Trans id="boatsList.noBoatsIn" />
                  {placeName} <Trans id="boatsList.found" />{' '}
                </span>
              )}
            </BoatsNumber>
          )}

          <BoatsSort />
        </TopWrapper>
      ) : null}
      <ListWrapper isHorizontalView={isMapShown}>
        {boatsToShow.map((boat, id) => (
          <BoatItem
            key={id}
            hoveredResultId={hoveredResultId}
            setHoveredResultId={setHoveredResultId}
            isHorizontalView={isMapShown}
            boatData={boat}
          />
        ))}
      </ListWrapper>
      {showPagination ? (
        <PaginateWrapper>
          <ReactPaginate
            previousLabel={
              <Img className="left" src={`${publicRuntimeConfig.ASSETS_PREFIX}/images/icons/paggination_arrow.png`} />
            }
            nextLabel={<Img src={`${publicRuntimeConfig.ASSETS_PREFIX}/images/icons/paggination_arrow.png`} />}
            breakLabel={'...'}
            pageCount={pageCount}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePageClick}
            containerClassName={'pagination'}
            previousLinkClassName={'pagination__link'}
            nextLinkClassName={'pagination__link'}
            disabledClassName={'pagination__link--disabled'}
            activeClassName={'pagination__link--active'}
            forcePage={selectedPage}
          />
        </PaginateWrapper>
      ) : null}
    </BoatsListWrapper>
  );
}

const BoatsListWrapper = styled.div<{ isMapShownFullScreen: boolean; isMapShown: boolean }>`
  display: ${(props) => (props.isMapShownFullScreen && props.isMapShown ? 'none' : 'block')};
`;

const ListWrapper = styled.div<{ isHorizontalView: boolean }>`
  display: flex;
  flex-flow: row wrap;

  justify-content: ${(props) => (props.isHorizontalView ? 'stretch' : 'space-between')};
`;

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1em;
  padding: 0 25px;
  padding-left: 3em;
  padding-right: 2.5em;

  @media (max-width: 640px) {
    padding-right: 0px;
    padding-left: 0px;
    font-size: 14px;
  }
`;

const BoatsNumber = styled.div`
  font-size: 1.5em;
  font-weight: 900;
  width: 100%;
  margin-right: 20px;

  &.hide {
    visibility: hidden;
  }
`;

const Img = styled.img`
  width: 32px;
  vertical-align: middle;
  height: 100%;
  &.left {
    transform: rotate(180deg);
  }
`;

const PaginateWrapper = styled.div`
  margin-top: 80px;
  margin-bottom: 30px;
  .pagination {
    display: flex;
    justify-content: center;
    list-style: none;
    cursor: pointer;
    align-items: center;
  }

  .pagination a {
    padding: 0 1em;
    border-radius: 5px;
    color: #0751c2;
    font-size: 16px;

    &:focus {
      outline: none;
    }
  }

  .pagination__link {
    font-weight: bold;
  }

  .pagination__link--active a {
    color: #8e9697 !important;
    cursor: inherit;
    pointer-events: none;
  }

  .pagination__link--disabled a {
    opacity: 0.2;
  }
`;
